import { useEffect, useState } from 'react'
import { useLocation } from 'react-use'
import { useRouter } from 'next/router'
import { Image } from '~/components/Image'
import { useCustomer, usePage, useSession } from '~/hooks'
import { GTM, GTMEvent } from '~/lib'
import jaycarLogoBlack from '~/public/images/jaycar-logo-black.svg'
import rtmLogoBlack from '~/public/images/rtm-logo-black.svg'

import { Drawer } from '../Drawer'
import styles from './AuthenticationDrawer.module.scss'
import { AuthenticationDrawersTypes } from './AuthenticationDrawer.types'
import ForgotPasswordAuthenticationDrawer from './ForgotPasswordAuthenticationDrawer'
import ResetPasswordAuthenticationDrawer from './ResetPasswordAuthenticationDrawer'
import SignInAuthenticationDrawer from './SignInAuthenticationDrawer'
import { SignUpAuthenticationDrawer } from './SignUpAuthenticationDrawer'

const REDIRECT_ID_LENGTH = -5

const isValidAuthenticationHash = (hash: string) => {
  return (
    !!hash && Object.values<string>(AuthenticationDrawersTypes).includes(hash)
  )
}

type AuthenticationDrawerProps = {
  enabledPaymentMethods?: string[]
}

const AuthenticationDrawer = ({
  enabledPaymentMethods,
}: AuthenticationDrawerProps) => {
  const [selectedDrawer, setSelectedDrawer] = useState(null)
  const router = useRouter()
  const location = useLocation()
  const hashValue = router?.asPath?.split('#')?.[1]
  const urlWithoutHash = router?.asPath?.split('#')?.[0]
  const page = usePage()
  const isRTM = page.brand === 'rtm'
  const { customer, defaultCustomerAddress } = useCustomer()
  const { session } = useSession()

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const hash = url.split('#')?.[1]
      if (isValidAuthenticationHash(hash)) {
        setSelectedDrawer(hash)
      } else {
        setSelectedDrawer(null)
      }
    }
    router.events.on('hashChangeStart', handleRouteChange)
    return () => {
      router.events.off('hashChangeStart', handleRouteChange)
    }
  }, [router])

  useEffect(() => {
    if (isValidAuthenticationHash(hashValue)) {
      setSelectedDrawer(hashValue)
    }
  }, [hashValue])

  const getRedirectUrl = () => {
    const { redirectUrl } = router.query || {}
    const newUrl = redirectUrl?.toString() || location.pathname
    const queryParams = new URLSearchParams(location.search)

    if (redirectUrl) {
      queryParams.delete('redirectUrl')
    } else {
      /* random query parameter to force the route (not the page) to reload */
      const randomString = new Date()
        .getTime()
        .toString()
        .slice(-REDIRECT_ID_LENGTH)
      queryParams.set('redirectId', randomString)
    }

    const totalQueryParams = Array.from(queryParams.values()).length

    if (totalQueryParams !== 0) {
      return `${newUrl}?${queryParams.toString()}`
    }

    return newUrl
  }

  const handleOnClose = (isRedirect = false) => {
    setSelectedDrawer(null)
    const redirectUrl = isRedirect ? getRedirectUrl() : urlWithoutHash

    router.replace(redirectUrl, undefined, {
      scroll: false,
    })
  }

  const drawerTitle = (
    <div className={styles.titleContainer}>
      {!isRTM ? (
        <Image
          src={jaycarLogoBlack}
          alt='Jaycar Logo'
          width={120}
          height={32}
        />
      ) : (
        <Image src={rtmLogoBlack} alt='RTM Logo' width={120} height={32} />
      )}
    </div>
  )

  useEffect(() => {
    session?.token?.isLoggedIn &&
      customer?.email &&
      GTM.dispatch(GTMEvent.USER_IDENTIFY, {
        customer,
        defaultCustomerAddress,
      })
  }, [customer, defaultCustomerAddress, session?.token?.isLoggedIn])

  return (
    <Drawer
      wrapperClass={styles.drawerWrapper}
      title={drawerTitle}
      isOpen={!!selectedDrawer}
      theme={{
        close: '!ml-0 !right-0',
        title: 'grow',
        inner: 'overflow-auto',
      }}
      placement='right'
      onClose={() => {
        return handleOnClose(false)
      }}>
      {(() => {
        switch (selectedDrawer) {
          case AuthenticationDrawersTypes.Login:
            return (
              <SignInAuthenticationDrawer
                onClose={handleOnClose}
                accountDrawerConfiguration={page.accountDrawerConfiguration}
              />
            )
          case AuthenticationDrawersTypes.Register:
            return (
              <SignUpAuthenticationDrawer
                onClose={handleOnClose}
                enabledPaymentMethods={enabledPaymentMethods}
              />
            )
          case AuthenticationDrawersTypes.ForgotPassword:
            return (
              <ForgotPasswordAuthenticationDrawer onClose={handleOnClose} />
            )
          case AuthenticationDrawersTypes.ResetPassword:
            return <ResetPasswordAuthenticationDrawer />
          default:
            return null
        }
      })()}
    </Drawer>
  )
}

export default AuthenticationDrawer
